import ModeEditOutlinedIcon from '@mui/icons-material/ModeEditOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
	Typography,
	Stack,
	Avatar,
	IconButton,
	Grid,
	TextField,
	Tooltip,
} from '@mui/material';

import React, { useState, useEffect, useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { ReactComponent as SearchOutlined } from 'common/assets/images/search-outline.svg';
import TableComponent from 'common/components/Table/Table';
import { PAGINATION_CONSTANTS } from 'common/constants';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { APP_GENERAL_ERROR_MSG } from 'common/constants/TextContent';
import { setToastMessage } from 'common/store/reducer/commonReducer';
import { toSentenceCase } from 'common/utils/utilFunctions';

import ChipComponent from '../../../common/components/Chip/Chip';
import { Column } from '../../../common/components/Table/CommonTableStyles';
import AlertComponent from '../../../common/components/ToastMessage/ToastMessage';
import CustomUserGridToolBar from '../../../common/components/Toolbar/Toolbar';
import { UserStatus } from '../../../common/constants/UserManagementConstants';
import { RootState } from '../../../common/store/AppStore';
import {
	setAllUsersList,
	setSearchUsers,
} from '../../../common/store/reducer/UserManagementListReducer';

import { getAllUsersList } from './api';
import { IUser } from './api/type-def';
import EditUser from './EditUser/EditUser';
import UserColumnLastActive from './UserColumnLastActive';

import styles from './UserManagementList.module.scss';

const UserManagementList = () => {
	const dispatch = useDispatch();

	const { paddingStyle } = styles;
	const { PAGE_SIZE, PAGE_NUMBER } = PAGINATION_CONSTANTS;
	const { ACTIVE } = UserStatus;

	const { OK } = HTTP_STATUS_CODES;

	const allUsersList: IUser[] = useSelector(
		(state: RootState) => state.usersListmgmt.allUsersList,
	);
	const usersList: IUser[] = useSelector(
		(state: RootState) => state.usersListmgmt.usersList,
	);
	const [isLoading, setLoading] = useState<boolean>(false);
	const { isToastOpen, message, severity } = useSelector(
		(state: RootState) => state.common.toastMessage,
	);
	const [searchText, setSearchText] = useState('');
	const [totalRecordsCount, setTotalRecordCount] = useState<number>(0);
	const [editData, setEditData] = useState<any>();
	const [openEditModal, setopenEditModal] = useState(false);
	const [paramsData] = useState({
		sortKey: '',
		pageSize: 500,
		pageNumber: PAGE_NUMBER,
		sortDirection: '',
	});

	const editUser = useCallback((rowData: any) => {
		setopenEditModal(true);
		setEditData(rowData);
	}, []);

	const closeEditModal = useCallback(() => {
		// call get all users api and dispatch set all users
		const getAllUsers = async () => {
			const resp = await getAllUsersList(paramsData);

			if (resp.responseBody) {
				const tempUsers = resp.responseBody;
				// add unique id
				const allTempUsers = tempUsers.content.map((ele: IUser) => {
					ele.id = ele.userId;
					return ele;
				});
				dispatch(setAllUsersList(allTempUsers));
			}
		};
		getAllUsers();
		setopenEditModal(false);
	}, [paramsData]);
	const usersListColumns: Column[] = [
		{
			id: 'firstName',
			label: 'User',
			disablePadding: false,
			isSortable: true,
			render: (params: any) => (
				<Stack direction="row" spacing={1}>
					<Avatar className={styles['user-avatar']}>
						<div>
							{`${params?.firstName?.charAt(0)}${params?.lastName?.charAt(0)}`}
						</div>
					</Avatar>
					<Stack direction="column" className={styles['user-column']}>
						<Typography>{params?.firstName}</Typography>
						<Typography className={styles['user-email-column']}>
							{params?.email}
						</Typography>
					</Stack>
				</Stack>
			),
		},

		{
			id: 'groups',
			label: 'Groups',
			disablePadding: false,
			render: (params: any) => {
				const tempGroups: any = [];
				if (params?.groups && params?.groups.length > 0) {
					params?.groups.forEach((ele: any) => {
						tempGroups.push(ele?.groupName);
					});
				}
				const tGroups = tempGroups.toString();
				const groupsAssigned = tGroups?.split(',');
				let tgroups = '';
				let extraCount = 0;
				if (groupsAssigned.length > 1) {
					tgroups = `${groupsAssigned[0]}`;
					extraCount = groupsAssigned.length - 1;
				} else {
					tgroups = tempGroups.toString();
				}

				return (
					<Typography className={styles['table-column-cell']}>
						{tgroups && (
							<div>
								<span>{`${tgroups}`}</span>

								{groupsAssigned.length > 1 && (
									<Tooltip
										title={
											<ul data-testid="groupIds">
												{groupsAssigned.map((group: any) => (
													<li key={group} className={styles['user-list-style']}>
														{group}
													</li>
												))}
											</ul>
										}
										arrow
										placement="right"
									>
										<span
											role="presentation"
											data-testid="group-item"
											onClick={() => {}}
										>{`..+${extraCount}`}</span>
									</Tooltip>
								)}
							</div>
						)}
					</Typography>
				);
			},
		},
		{
			id: 'roles',
			label: 'Roles',
			disablePadding: false,
			render: (params: any) => {
				const tempRoles: any = [];
				if (params?.roles && params?.roles.length > 0) {
					params?.roles.forEach((ele: any) => {
						tempRoles.push(ele?.roleName);
					});
				}
				const tRoles = tempRoles.toString();
				const rolesAssigned = tRoles?.split(',');

				let troles = '';

				let extraCount = 0;
				if (rolesAssigned.length > 1) {
					troles = `${rolesAssigned[0]}`;
					extraCount = rolesAssigned.length - 1;
				} else {
					troles = tRoles;
				}

				return (
					<Typography className={styles['table-column-cell']}>
						{troles && (
							<div>
								<span>{`${troles}`}</span>

								{rolesAssigned.length > 1 && (
									<Tooltip
										title={
											<ul>
												{rolesAssigned.map((group: any) => (
													<li key={group} className={styles['user-list-style']}>
														{group}
													</li>
												))}
											</ul>
										}
										arrow
										placement="right"
									>
										<span
											role="presentation"
											data-testid="role-item"
											onClick={() => {}}
										>{`..+${extraCount}`}</span>
									</Tooltip>
								)}
							</div>
						)}
					</Typography>
				);
			},
		},
		{
			id: 'userEndDate',
			label: toSentenceCase('End Date'),
			disablePadding: false,
			isSortable: true,
			render: (params: any) => (
				<UserColumnLastActive lastActiveField={params?.userEndDate} />
			),
		},
		{
			id: 'status',
			label: 'Status',
			disablePadding: false,
			isSortable: true,
			render: (params: any) => (
				<ChipComponent
					customcolor={params?.status}
					label={params?.status}
					color={params?.status === ACTIVE ? 'success' : undefined}
				/>
			),
		},
		{
			id: 'lastActive',
			label: toSentenceCase('Last Active'),
			disablePadding: false,
			isSortable: true,
			render: (params: any) => (
				<UserColumnLastActive lastActiveField={params?.lastActive} />
			),
		},
		{
			id: 'actions',
			label: '',
			disablePadding: false,
			render: (params: any) => {
				const handleClick = () => {
					editUser(params);
				};
				return (
					<Stack direction="row" justifyContent="space-between">
						<IconButton onClick={handleClick} data-testid="edit-icon">
							<ModeEditOutlinedIcon />
						</IconButton>
						<IconButton>
							<MoreVertIcon />
						</IconButton>
					</Stack>
				);
			},
		},
	];

	const handleClose = useCallback(() => {
		// close the toast message
		setToastMessage({
			message: '',
			severity: '',
			isToastOpen: false,
		});
		dispatch(
			setToastMessage({
				message: '',
				severity: '',
				isToastOpen: false,
			}),
		);
	}, []);

	const onSerachTextChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			setSearchText(e.target.value);
		},
		[],
	);

	// Add users to store on search
	useEffect(() => {
		const getSearchData = setTimeout(() => {
			if (usersList.length > 0) {
				const searchListUsers = allUsersList.filter((ele: IUser) => {
					const { firstName, email } = ele;
					return (
						firstName?.toLowerCase().includes(searchText.toLowerCase()) ||
						email?.toLowerCase().includes(searchText.toLowerCase())
					);
				});
				dispatch(setSearchUsers(searchListUsers));
			}
		}, 300);

		return () => clearTimeout(getSearchData);
	}, [searchText]);
	// Add users to store after getting response

	const getAllUsers = async (data: any) => {
		setLoading(true);
		const resp = await getAllUsersList(data);
		if (resp?.statusCode === OK) {
			if (resp?.responseBody && resp?.responseBody?.content.length > 0) {
				const tempUsers = resp.responseBody;
				const allTempUsers = tempUsers.content.map((ele: IUser) => {
					ele.id = ele.userId;
					return ele;
				});
				dispatch(setAllUsersList(allTempUsers));
				setTotalRecordCount(tempUsers?.totalCount);
				setLoading(false);
			} else {
				dispatch(setAllUsersList([]));
				setLoading(false);
			}
		} else {
			dispatch(setAllUsersList([]));
			setLoading(false);
			dispatch(
				setToastMessage({
					message: APP_GENERAL_ERROR_MSG,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		}
	};

	const handlePageChange = (data: any) => {
		getAllUsers(data);
	};

	const handleSortChange = (data: any) => {
		getAllUsers(data);
	};

	const onUserHandlerAction = () => {
		const paramsData = {
			sortKey: '',
			pageSize: PAGE_SIZE,
			pageNumber: PAGE_NUMBER,
			sortDirection: '',
		};
		getAllUsers(paramsData);
	};

	return (
		<Grid
			container
			data-testid="users-main-box"
			className={styles['users-mgmt-list']}
		>
			<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
				<Grid item xs={10} sm={10} md={6} lg={6} xl={6} sx={{ marginTop: 3 }}>
					<TextField
						sx={{ width: 520 }}
						variant="outlined"
						data-testid="user-search-id"
						placeholder=""
						InputProps={{
							sx: { paddingLeft: paddingStyle },
							startAdornment: (
								<IconButton>
									<SearchOutlined className={styles['input-search-icon']} />
								</IconButton>
							),
						}}
						size="small"
						onChange={onSerachTextChange}
						className={styles['searchbar-textfield']}
					/>
				</Grid>
			</Grid>
			<Grid
				item
				xs={12}
				sm={12}
				md={12}
				lg={12}
				xl={12}
				className={styles['user-guide-toolbar']}
			>
				<CustomUserGridToolBar onUserAction={onUserHandlerAction} />
			</Grid>
			<Grid item xs={12} sm={12} md={12} lg={12} xl={12} sx={{ marginTop: 1 }}>
				{usersList && (
					<TableComponent
						rows={usersList}
						columns={usersListColumns}
						totalItems={totalRecordsCount}
						defaultSortKey=""
						onPageHandler={handlePageChange}
						onSortHandler={handleSortChange}
						isLoading={isLoading}
						tableStyles={{ minWidth: '46.875em', maxHeight: '27.5em' }}
						enableHeaderColor
						isRenderPagination
					/>
				)}
			</Grid>

			{openEditModal ? (
				<div>
					<EditUser data={editData} onCloseModal={closeEditModal} />
				</div>
			) : null}
			<AlertComponent
				open={isToastOpen ?? false}
				toastMessage={message ?? ''}
				severity={severity ?? ''}
				handleCloseMessage={handleClose}
				vertical="top"
				horizontal="right"
			/>
		</Grid>
	);
};

export default UserManagementList;
